/**
 * CockpitLite — de hoofdpagina (/). Een aanpasbaar widget-dashboard: groet +
 * een vrij in te delen grid van widgets (max 3 kolommen). Rechtsboven een
 * editor-toggle; in edit-modus kun je widgets verslepen, breder/hoger maken,
 * instellen, verwijderen en toevoegen. De indeling wordt per gebruiker bewaard.
 *
 * Widgets + het designprincipe staan in ./widgets (zie docs/widget-design-principles.md).
 */
import React, { useState } from 'react';
import { Box, Typography, CircularProgress, Button, Menu, MenuItem } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { Link as RouterLink } from 'react-router-dom';
import useDashboardLayout from './widgets/useDashboardLayout';
import WidgetBoard from './widgets/WidgetBoard';
import { T, Icon } from './widgets/widgetTokens';

const greet = () => {
  const h = new Date().getHours();
  return h < 6 ? 'Goedenacht' : h < 12 ? 'Goedemorgen' : h < 18 ? 'Goedemiddag' : 'Goedenavond';
};

function TemplatePicker({ templates, templateKey, customized, onPick }) {
  const [anchor, setAnchor] = useState(null);
  const current = templates.find((t) => t.key === templateKey);
  if (!templates.length) return null;
  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<Icon name={current?.icon || 'dashboard'} size={17} />}
        endIcon={<Icon name="expand_more" size={16} />}
        sx={{
          fontFamily: T.fontBody, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
          color: T.secondary, border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface, px: 1.5, py: 0.65,
          '&:hover': { borderColor: T.tertiary, color: T.tertiary },
        }}
      >
        {current?.name || 'Template'}{customized ? ' ·' : ''}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {templates.map((t) => (
          <MenuItem key={t.key} selected={t.key === templateKey}
            onClick={() => { onPick(t.key); setAnchor(null); }}
            sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', gap: 1.25, minWidth: 200 }}>
            <Icon name={t.icon || 'dashboard'} size={18} sx={{ color: T.tertiary }} /> {t.name}
          </MenuItem>
        ))}
        {customized && (
          <MenuItem disabled sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
            Je hebt eigen aanpassingen — "Reset" zet het template terug.
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default function CockpitLite() {
  const { accounts } = useMsal();
  const userName = accounts?.[0]?.name || '';
  const voornaam = userName?.split(' ')[0] || 'collega';

  const {
    widgets, loading, templates, templateKey, customized,
    reorder, updateWidget, removeWidget, addWidgetAt, applyTemplate, resetToDefault,
  } = useDashboardLayout();
  const [editMode, setEditMode] = useState(false);

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: 'calc(100vh - 44px)' }}>
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

        {/* ── Kop: groet + editor-toggle ── */}
        <Box sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 2, mb: { xs: 3, md: 4 },
        }}>
          <Box>
            <Typography sx={{
              fontFamily: T.font, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' },
              fontWeight: 800, color: T.text, letterSpacing: '-0.03em', lineHeight: 1.1,
            }}>
              {greet()}, {voornaam}
            </Typography>
            {editMode && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.95rem', color: T.secondary, mt: 1 }}>
                Sleep, schaal en stel je widgets in. Wijzigingen worden bewaard.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, mt: 0.5 }}>
            {editMode ? (
              <>
                <TemplatePicker templates={templates} templateKey={templateKey} customized={customized} onPick={applyTemplate} />
                <Button
                  onClick={resetToDefault}
                  startIcon={<Icon name="restart_alt" size={17} />}
                  sx={{
                    fontFamily: T.fontBody, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
                    color: T.outline, '&:hover': { bgcolor: T.surfaceHigh },
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained" disableElevation
                  onClick={() => setEditMode(false)}
                  startIcon={<Icon name="check" size={17} />}
                  sx={{
                    fontFamily: T.fontBody, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
                    bgcolor: T.emerald, '&:hover': { bgcolor: '#0e9f6e' },
                  }}
                >
                  Klaar
                </Button>
              </>
            ) : (
              <>
              <Button
                component={RouterLink} to="/dashboard"
                startIcon={<Icon name="person" size={17} />}
                sx={{
                  fontFamily: T.fontBody, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
                  color: T.secondary, border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface,
                  boxShadow: T.shadow, px: 2, py: 0.75, mr: 1,
                  '&:hover': { borderColor: T.tertiary, color: T.tertiary, bgcolor: T.surface },
                }}
              >
                Mijn Dashboard
              </Button>
              <Button
                onClick={() => setEditMode(true)}
                startIcon={<Icon name="tune" size={17} />}
                sx={{
                  fontFamily: T.fontBody, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
                  color: T.secondary, border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface,
                  boxShadow: T.shadow, px: 2, py: 0.75,
                  '&:hover': { borderColor: T.tertiary, color: T.tertiary, bgcolor: T.surface },
                }}
              >
                Bewerken
              </Button>
              </>
            )}
          </Box>
        </Box>

        {/* ── Widget-grid ── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
            <CircularProgress size={20} sx={{ color: T.outlineVariant }} />
          </Box>
        ) : (
          <WidgetBoard
            widgets={widgets}
            editMode={editMode}
            reorder={reorder}
            updateWidget={updateWidget}
            removeWidget={removeWidget}
            addWidgetAt={addWidgetAt}
          />
        )}

      </Box>
    </Box>
  );
}
